/** @module application */

import HR from 'headroom.js';
import $ from 'jquery';

const _headroomSpacing = Symbol();

/**
 * Wrapper for package headroom.js
 *
 * @param {object} [options] - Initial options
 * @param {string|HTMLElement} [options.headroom='.headroom'] - Element or selector to apply headroom to.
 * @param {boolean|string|HTMLElement} [options.prependSpacerTo=false] - Whether and where to insert the spacer to avoid jumping content.
 * @param {string} [options.breakpoint=null] - Optional name of a breakpoint (as defined in <code>resources/scss/__vars/_breakpoints.scss</code>), where the functionality should be toggled. If set, the header is static below the given breakpoint and dynamic above it.
 * @param {object} [options.classes] - Classes used by headroom.
 * @param {string} [options.classes.initial='headroom--initialized'] - Class to apply to <code>$headroom</code> when headroom is loaded.
 * @param {string} [options.classes.pinned='headroom--fixed'] - Class to apply to <code>$headroom</code> when headroom is pinned.
 * @param {string} [options.classes.unpinned='headroom--unfixed'] - Class to apply to <code>$headroom</code> when headroom is released.
 * @param {string} [options.classes.top='headroom--top'] - Class to apply to <code>$headroom</code> when the page is scrolled to top.
 * @param {string} [options.classes.notTop='headroom--notTop'] - Class to apply to <code>$headroom</code> when the page is not scrolled to top.
 * @param {string} [options.classes.bottom='headroom--bottom'] - Class to apply to <code>$headroom</code> when the page is scrolled to bottom.
 * @param {string} [options.classes.notBottom='headroom--notBottom'] - Class to apply to <code>$headroom</code> when the page is not scrolled to bottom.
 * @param {int|string|function|object} [options.offset] - Defines offsets to apply to scrolling upwards and downwards, optionally for different breakpoints. If not of type <code>object</code>, settings apply to both directions and any breakpoint.
 * @param {int|string|function} [options.offset.up='[data-headroom-offset]'] - The offset - or an element to measure the offset from, or a callback returning the offset - to apply when scrolling upwards. Applies to all breakpoints.
 * @param {int|string|function} [options.offset.down=0] - The offset - or an element to measure the offset from, or a callback returning the offset - to apply when scrolling downwards. Applies to all breakpoints.
 * @param {int|string|function} [options.offset.<breakpoint>] - Offset-settings for specific breakpoints (as defined in resources/scss/ __vars/_breakpoints.scss). See {@link options.offset} for options.
 * @example - TODO: examples need to be added when the feature is used.
 * @see http://wicky.nillia.ms/headroom.js/
 */
export class Headroom {
    constructor(options) {
        this.defaultOptions = {
            headroom: '.headroom',
            prependSpacerTo: false,
            breakpoint: null,
            offset: 0,
            classes: {
                initial: 'headroom--initialized',
                pinned: 'headroom--fixed',
                unpinned: 'headroom--unfixed',
                top: 'headroom--top',
                notTop: 'headroom--not-top',
                bottom: 'headroom--bottom',
                notBottom: 'headroom--not-bottom',
            },
        };

        this.options = { ...this.defaultOptions, ...options };

        this._initOffset();

        const settings = this.options;

        if (typeof settings.headroom === 'string') {
            settings.headroom = document.querySelector(settings.headroom);
        }

        if (!settings.headroom) {
            return; // exit gracefully
        }

        if (typeof settings.prependSpacerTo === 'string') {
            settings.prependSpacerTo = document.querySelector(settings.prependSpacerTo);
        }

        if (settings.prependSpacerTo) {
            let spacing = document.createElement('div');

            spacing.classList.add('headroom-spacing');

            if (settings.breakpoint === null || Foundation.MediaQuery.atLeast(settings.breakpoint)) {
                spacing.style.paddingTop = settings.headroom.offsetHeight + 'px';

                // set the class early to avoid a jumpy UX
                settings.headroom.classList.add(settings.classes.initial);
            }

            settings.prependSpacerTo.insertAdjacentElement('afterbegin', spacing);
            this[_headroomSpacing] = spacing;
        }

        // headroom is configured to scroll the header out with the page if the page is on top
        // but shows/hides immediately when mid-page
        this._instance = new HR(settings.headroom, {
            offset: settings.headroom.offsetHeight + this._getScrollOffset(),
            tolerance: 20, // mostly used for touch-pads/-screens
            classes: settings.classes,
            onTop: () => {
                settings.headroom.classList.remove(settings.classes.pinned);
                this._instance.offset = settings.headroom.offsetHeight * 2 + this._getScrollOffset();
            },
            onPin: () => {
                this._instance.offset = this._getScrollOffset();
            },
        });

        if (settings.breakpoint !== null) {
            settings.headroom.dataset.breakpoint = settings.breakpoint;

            // jquery is required to trigger *this* custom event
            /* eslint-disable jquery/no-trigger */
            $(window)
                .on('changed.zf.mediaquery', () => {
                    // newSize is the name of the now-current breakpoint, oldSize is the previous breakpoint
                    if (Foundation.MediaQuery.atLeast(settings.breakpoint)) {
                        this.init();
                    } else {
                        this.destroy();
                    }
                })
                .trigger('changed.zf.mediaquery');
            /* eslint-enable jquery/no-trigger */
        } else {
            settings.headroom.dataset.breakpoint = 'false';

            this.init();
        }
    }

    _initOffset() {
        let offset = this.options.offset;

        if (typeof offset !== 'object') {
            offset = this.options.offset = {
                small: {
                    up: offset,
                    down: offset,
                },
            };
        } else if ('up' in offset || 'down' in offset) {
            offset = this.options.offset = {
                small: offset,
            };
        }

        for (const breakpoint in offset) {
            if (Object.prototype.hasOwnProperty.call(offset, breakpoint)) {
                let val = offset[breakpoint];

                if (typeof val !== 'object') {
                    offset[breakpoint] = {
                        up: Number(val),
                        down: Number(val),
                    };
                } else {
                    offset[breakpoint] = {
                        up: isNaN(val.up) ? document.querySelector(val.up) : Number(val.up),
                        down: isNaN(val.down) ? document.querySelector(val.down) : Number(val.down),
                    };
                }
            }
        }

        this.options.offset = offset;
    }

    _getScrollOffset() {
        let options = this.options,
            offset = 0,
            target = typeof options.offset === 'string' ? document.querySelector(options.offset) : options.offset,
            dir;

        if (!target) {
            return offset;
        }

        dir = target.offsetTop < Math.max(document.documentElement.scrollTop, document.body.scrollTop) ? 'up' : 'down';

        for (const query of [...Foundation.MediaQuery.queries].reverse()) {
            if (query.name in options.offset && Foundation.MediaQuery.atLeast(query.name)) {
                let offsetConfig = options.offset[query.name][dir];

                if (typeof offsetConfig === 'function') {
                    offset = offsetConfig();
                } else if (typeof offsetConfig === 'object') {
                    offset = offsetConfig.getBoundingClientRect().height;
                } else {
                    offset = offsetConfig;
                }

                break;
            }
        }

        return offset;
    }

    /**
     * Shows (= pins) the header.
     */
    show() {
        if (this._instance.initialised !== true) {
            return;
        }

        // api: need to be unpinned before you can pin the header
        this.hide();

        if (this._instance) {
            this._instance.pin();
        }
    }

    /**
     * Hides (= unpins) the header.
     */
    hide() {
        if (this._instance) {
            this._instance.unpin();
        }
    }

    /**
     * Initializes the Headroom instance.
     */
    init() {
        document.documentElement.classList.add('headroom--active');

        if (this._instance && this._instance.initialised !== true) {
            this._instance.init();

            if (!this.__resizeHandler) {
                this.__resizeHandler = this._onResize.bind(this);
            }

            window.addEventListener('resize', this.__resizeHandler, { passive: true });
            this._updateHeadroomSpacing();
        }
    }

    /**
     * Destroys the Headroom instance.
     */
    destroy() {
        document.documentElement.classList.remove('headroom--active');

        if (this._instance && this._instance.initialised === true) {
            this._instance.destroy();

            if (!this.__resizeHandler) {
                this.__resizeHandler = this._onResize.bind(this);
            }

            window.removeEventListener('resize', this.__resizeHandler, { passive: true });
            this._updateHeadroomSpacing(true);
        }
    }

    /**
     * Handler called upon resizing the browser window.
     * @private
     */
    _onResize() {
        this._updateHeadroomSpacing();
    }

    /**
     * Handles the height the spacer should acquire.
     * @param {boolean} [reset=false] - Whether to update the spacer, or reset it (setting it to 0px).
     * @private
     */
    _updateHeadroomSpacing(reset = false) {
        if (this[_headroomSpacing]) {
            this[_headroomSpacing].style.paddingTop = reset ? '0px' : this.options.headroom.offsetHeight + 'px';
        }
    }
}
